"use client";

import { FILTER_BAR_ID } from "../../hooks/useCollectionScroll";
import { getLifestyleFilterBarTop } from "../../lifestyle-sticky";
import type useLifestyleFilters from "../../hooks/useLifestyleFilters";
import { CATEGORY_LABELS } from "../../lifestyle-constants";
import type { FilterModalTab } from "../filterModal/FilterOptionsModal";
import { CategoryGridIcon } from "../lifestyleIcons";
import AllFiltersButton from "./AllFiltersButton";
import FilterModalButton from "./FilterModalButton";
import SearchInput from "./SearchInput";
import SortDropdown from "./SortDropdown";

interface FilterBarProps {
  filters: ReturnType<typeof useLifestyleFilters>;
  onOpenDrawer: () => void;
  onOpenFilterModal: (tab: FilterModalTab) => void;
  onOpenSortSheet: () => void;
}

/**
 * Sticky control strip rendered below the hero. It scrolls with the page until
 * the user passes it, then sticks under the header + countdown stack.
 * `--header-height` and `--lifestyle-countdown-height` publish those offsets.
 *
 * Collections and Categories open a shared modal rather than inline menus —
 * the sticky bar is its own stacking context, so that modal has to be rendered
 * by the page to sit above the site header. The id is how a collection jump
 * measures the offset it has to clear.
 */
export default function FilterBar({ filters, onOpenDrawer, onOpenFilterModal, onOpenSortSheet }: FilterBarProps) {
  const { category } = filters.filters;

  return (
    <div
      id={FILTER_BAR_ID}
      style={{ top: getLifestyleFilterBarTop() }}
      // Mobile alone carries a divider; desktop is the bare level-1 surface.
      className="sticky z-20 w-full px-1 lg:px-5 py-2 lg:py-3 bg-gray-4 border-b lg:border-b-0 border-black/[0.08]">
      <div className="flex items-center w-full max-w-[1320px] mx-auto gap-2 lg:gap-6">
        {/* Mobile drops Collections; the categories tile opens the same modal,
            whose tabs are how collections stay reachable there. Desktop pairs
            them inside a 340px track, so they share the width evenly. */}
        <div className="flex items-center shrink-0 lg:w-[340px] gap-2">
          <FilterModalButton
            label="Collections"
            className="hidden lg:flex lg:flex-1 lg:min-w-0"
            onClick={() => onOpenFilterModal("collections")}
          />
          <FilterModalButton
            label="Categories"
            selectedLabel={category ? CATEGORY_LABELS[category] : null}
            compact
            icon={<CategoryGridIcon className="w-4 h-4" />}
            className="lg:flex-1 lg:min-w-0"
            onClick={() => onOpenFilterModal("categories")}
          />
        </div>
        <SearchInput
          value={filters.filters.keyword}
          suggestionPool={filters.suggestionPool}
          onChange={filters.setKeyword}
        />
        <div className="flex items-center shrink-0 gap-2">
          <SortDropdown selected={filters.filters.sort} onSelect={filters.setSort} onOpenSheet={onOpenSortSheet} />
          <AllFiltersButton activeCount={filters.activeFilterCount} onClick={onOpenDrawer} />
        </div>
      </div>
    </div>
  );
}
